// Phase 14e-c-3 — /admin/notifications page.
//
// Top of page: count of "needs attention" notifications + "Run healing
// scan" button. Filter chips for status. Paginated AdminTable. Row
// click → NotificationDetailModal showing deliveries.
//
// "Needs attention" criterion mirrors the backend healing scan in
// apps/backend/src/notifications/notifications-healing.service.ts
// (status=QUEUED AND createdAt < now-STALE_THRESHOLD AND dispatchedAt IS NULL),
// applied as `status=QUEUED` server-side + a client-side createdAt
// staleness check so the UI matches what the healing scan would
// actually re-enqueue. (audit UX-H — the notification status enum is
// QUEUED|COMPLETED; the prior PENDING/SENT/FAILED/SUPPRESSED filter 400'd.)

import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input, StatusBadge, humanizeEnum, useFocusTrap } from '@truecare/ui';
import { ApiError, messageOf, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { AdminFilterChips } from '../components/AdminFilterChips';
import { OverrideActionButton } from '../components/OverrideActionButton';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/notifications')({
  beforeLoad: () => adminBeforeLoad('/notifications'),
  component: NotificationsPage,
});

interface NotificationRow {
  id: string;
  codeName: string;
  // audit UX-H — the notification-level status enum is QUEUED | COMPLETED (see
  // NotificationStatus in schema.prisma). Pending/Sent/Failed/Suppressed are
  // DELIVERY-level states, not filterable via this endpoint.
  status: 'QUEUED' | 'COMPLETED';
  category: string;
  recipientEmail: string;
  recipientUserId: string | null;
  createdAt: string;
  dispatchedAt: string | null;
}

interface NotificationDelivery {
  id: string;
  channel: string;
  status: string;
  providerMessageId: string | null;
  attemptCount: number;
  lastErrorMessage: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NotificationDetail extends NotificationRow {
  sourceEventId: string;
  payload: Record<string, unknown>;
  updatedAt: string;
  deliveries: NotificationDelivery[];
}

// Audit MEDIUM — must mirror the backend's STALE_PENDING_THRESHOLD_MS
// (apps/backend/src/notifications/notifications-healing.service.ts): the sum of
// the retry-backoff budget (30s + 2m + 10m + 1h + 6h) + a 1h worker-pickup
// margin = 29,550,000 ms (~8h13m). The old 15-min value flagged deliveries as
// "needs attention" that the healing scan won't actually re-enqueue yet — a
// misleading queue. Keep in sync with the backend derivation.
const STALE_THRESHOLD_MS = 30_000 + 120_000 + 600_000 + 3_600_000 + 21_600_000 + 3_600_000;

type FilterMode = 'needs-attention' | 'all' | 'queued' | 'completed';

interface ReactivateResult {
  email: string;
  localRowsDeleted: number;
  postmarkCleared: boolean;
}

/**
 * Operator un-suppress control (#2). Clears an address from our local
 * notification_suppression table + Postmark's list so it can receive again —
 * recovers a contact that bounced/complained once but is now valid, instead of
 * being permanently dead-lettered. TotpFresh-guarded server-side; the modal's
 * step-up is handled by the global TotpStepUpModalProvider.
 */
function ReactivateSuppressionCard() {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<ReactivateResult | null>(null);

  const reactivate = useMutation({
    mutationFn: (input: { email: string; reason: string }) =>
      unwrap(
        api.POST('/api/v1/admin/suppressions/reactivate' as never, { body: input } as never),
      ) as Promise<ReactivateResult>,
    onSuccess: (data) => {
      setResult(data);
      setEmail('');
    },
  });

  const trimmed = email.trim();
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed);

  return (
    <Card padding="md">
      <p className="font-heading text-sm uppercase tracking-wide text-forest">
        Reactivate a suppressed address
      </p>
      <p className="mt-1 text-sm text-ink-muted">
        Removes an address from our suppression list and Postmark so it can receive email
        again — use when a contact bounced or was flagged once but is now valid.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem]">
          <Field label="Email address">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="name@example.com"
              data-testid="reactivate-email"
            />
          </Field>
        </div>
        <OverrideActionButton
          actionLabel="Reactivate suppressed address"
          disabled={!emailOk || reactivate.isPending}
          loading={reactivate.isPending}
          instructions={
            emailOk ? <>This re-opens delivery to {trimmed} across our pipeline and Postmark.</> : undefined
          }
          onConfirm={async ({ reason }) => {
            await reactivate.mutateAsync({ email: trimmed.toLowerCase(), reason });
          }}
          data-testid="reactivate-suppression"
        >
          Reactivate…
        </OverrideActionButton>
      </div>
      {reactivate.isError && (
        <Alert tone="danger" title="Reactivation failed" className="mt-3">
          {messageOf(reactivate.error)}
        </Alert>
      )}
      {result && (
        <p className="mt-2 text-xs text-success" data-testid="reactivate-result">
          Reactivated {result.email}: {result.localRowsDeleted} local suppression row
          {result.localRowsDeleted === 1 ? '' : 's'} cleared, Postmark{' '}
          {result.postmarkCleared ? 'cleared' : 'not cleared (check logs / token)'}.
        </p>
      )}
    </Card>
  );
}

function NotificationsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState<FilterMode>('needs-attention');
  const [detailId, setDetailId] = useState<string | null>(null);

  // Server-side status filter applied; the "needs-attention" mode queries
  // QUEUED + applies a client-side staleness check. QUEUED/COMPLETED are the
  // only values the backend's status filter accepts (audit UX-H — the old
  // PENDING/SENT/FAILED/SUPPRESSED values 400'd, breaking the default view).
  const serverStatus =
    filter === 'needs-attention' || filter === 'queued'
      ? 'QUEUED'
      : filter === 'completed'
        ? 'COMPLETED'
        : undefined;

  // "Needs attention" is a client-side staleness filter over the PENDING set,
  // so it can't be paginated server-side. A client filter over one server page
  // could only see that page — capping totalCount to the current page and
  // hiding stale deliveries past page 1. Mirror the payouts.tsx pattern: fetch
  // the full status-filtered set (one large page) and paginate the filtered
  // result client-side.
  const isNeedsAttention = filter === 'needs-attention';
  // MUST be ≤ the backend's NotificationListQuerySchema.limit.max(200), or the
  // default-view fetch 400s and the queue shows "Couldn't load" (verification
  // audit — the UX-H fix corrected the status enum but left this at 500, which
  // trips the same endpoint's limit cap). The daily+5-min healing cron re-queues
  // stuck deliveries, so >200 QUEUED-and-stale at once is unreachable at pilot
  // scale; if it ever truncates, the truncation note below fires.
  const NEEDS_ATTENTION_FETCH_CAP = 200;

  const listQuery = useQuery({
    queryKey: ['admin', 'notifications', { status: serverStatus, page, limit, isNeedsAttention }],
    queryFn: async () => {
      const queryParam: Record<string, string> = {
        page: String(isNeedsAttention ? 1 : page),
        limit: String(isNeedsAttention ? NEEDS_ATTENTION_FETCH_CAP : limit),
      };
      if (serverStatus) queryParam["status"] = serverStatus;

      const r = (await unwrap(
        api.GET('/api/v1/admin/notifications' as never, {
          params: { query: queryParam },
        } as never),
      )) as { items: NotificationRow[]; totalCount: number };
      return r;
    },
  });

  // Full client-side "needs attention" set — the staleness-filtered PENDING
  // rows across the whole fetched window (not just the current page).
  const needsAttentionMatched = useMemo(() => {
    const items = listQuery.data?.items ?? [];
    if (!isNeedsAttention) return items;
    const cutoff = Date.now() - STALE_THRESHOLD_MS;
    return items.filter(
      (n) =>
        n.status === 'QUEUED' &&
        n.dispatchedAt === null &&
        new Date(n.createdAt).getTime() < cutoff,
    );
  }, [listQuery.data, isNeedsAttention]);

  // When paginating client-side, slice the filtered set for the current page.
  const visibleItems = isNeedsAttention
    ? needsAttentionMatched.slice((page - 1) * limit, page * limit)
    : needsAttentionMatched;

  // totalCount must reflect the full filtered set, not the current page, so
  // AdminTable computes the real totalPages and the summary count is accurate.
  const effectiveTotalCount = isNeedsAttention
    ? needsAttentionMatched.length
    : (listQuery.data?.totalCount ?? 0);

  // Healing mutation — fires POST /admin/notifications/heal-stuck-deliveries.
  const healing = useMutation({
    mutationFn: async () =>
       
      (await unwrap(
        api.POST('/api/v1/admin/notifications/heal-stuck-deliveries' as never, {
          body: {},
        } as never),
      )) as { reEnqueuedCount: number; deliveryIds: string[] },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    },
  });

  const columns: AdminTableColumn<NotificationRow>[] = [
    {
      key: 'createdAt',
      header: 'Created',
      width: 'w-44',
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    { key: 'codeName', header: 'Template', width: 'w-48' },
    { key: 'status', header: 'Status', width: 'w-28' },
    { key: 'category', header: 'Category', width: 'w-32' },
    { key: 'recipientEmail', header: 'Recipient', width: 'w-64' },
    {
      key: 'dispatchedAt',
      header: 'Dispatched',
      width: 'w-44',
      render: (r) => (r.dispatchedAt ? new Date(r.dispatchedAt).toLocaleString() : '—'),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Notifications</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Every email and text message the platform sends. The healing scan
            re-sends any message that got stuck before it was delivered — it runs
            automatically once a day, and the button below runs it now.
          </p>
        </header>

        <ReactivateSuppressionCard />

        <Card padding="md">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-heading text-sm uppercase tracking-wide text-forest">
                Healing scan
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                {filter === 'needs-attention'
                  ? `${needsAttentionMatched.length} notification${
                      needsAttentionMatched.length === 1 ? '' : 's'
                    } with stuck PENDING deliveries.`
                  : 'Switch to "Needs attention" filter to see stuck-delivery count.'}
              </p>
              {isNeedsAttention &&
                (listQuery.data?.items.length ?? 0) >= NEEDS_ATTENTION_FETCH_CAP && (
                  <p className="mt-1 text-xs text-gold">
                    Showing the first {NEEDS_ATTENTION_FETCH_CAP} QUEUED notifications — more may
                    exist. Run the healing scan (it processes all stuck deliveries regardless).
                  </p>
                )}
              {healing.data && (
                <p className="mt-1 text-xs text-success">
                  Last scan re-enqueued {healing.data.reEnqueuedCount} deliver
                  {healing.data.reEnqueuedCount === 1 ? 'y' : 'ies'}.
                </p>
              )}
              {healing.isError && (
                <p className="mt-1 text-xs text-danger">
                  Healing scan failed: {healing.error instanceof ApiError ? healing.error.message : 'unknown error'}
                </p>
              )}
            </div>
            <Button
              variant="primary"
              onClick={() => healing.mutate()}
              loading={healing.isPending}
              data-testid="notifications-run-healing"
            >
              Run healing scan
            </Button>
          </div>
        </Card>

        <Card padding="md">
          <CardHeader title="Filters" />
          <div className="mt-3">
            <AdminFilterChips
              value={filter}
              options={
                [
                  ['needs-attention', 'Needs attention'],
                  ['queued', 'Queued'],
                  ['completed', 'Completed'],
                  ['all', 'All'],
                ] as const
              }
              onChange={(f) => {
                setFilter(f);
                setPage(1);
              }}
              ariaLabel="Filter notifications by status"
              testIdPrefix="notifications-filter"
            />
          </div>
          <div className="mt-4">
            <AdminTable
              columns={columns}
              rows={visibleItems}
              isLoading={listQuery.isPending}
              isError={listQuery.isError}
              onRetry={() => void listQuery.refetch()}
              emptyMessage={
                filter === 'needs-attention'
                  ? 'No notifications need attention. Healing scan would be a no-op.'
                  : 'No notifications match the current filter.'
              }
              page={page}
              limit={limit}
              totalCount={effectiveTotalCount}
              onPageChange={setPage}
              onLimitChange={(n) => {
            // 2026-07-23 audit (U4): resetting to page 1 is required — the
            // backends compute skip=(page-1)*pageSize with no clamping, so
            // growing the page size while on page 2+ can put skip past
            // totalCount and render "no results" over a populated list.
            setLimit(n);
            setPage(1);
          }}
              onRowClick={(r) => setDetailId(r.id)}
            />
          </div>
        </Card>

        {detailId && (
          <NotificationDetailModal
            notificationId={detailId}
            onClose={() => setDetailId(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ─── Detail modal ──────────────────────────────────────────────────────────

function NotificationDetailModal({
  notificationId,
  onClose,
}: {
  notificationId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'notifications', notificationId] as const,
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/notifications/{id}' as never, {
          params: { path: { id: notificationId } },
        } as never),
      )) as NotificationDetail;
      return r;
    },
  });

  // audit medium — trap focus + Escape-to-close via the shared hook.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-detail-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="notification-detail-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="w-full max-w-3xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <h2
            id="notification-detail-modal-title"
            className="font-heading text-xl text-forest"
          >
            Notification detail
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        {detailQuery.isPending && <p className="mt-4 text-sm">Loading…</p>}
        {detailQuery.isError && (
          <Alert tone="danger" className="mt-3">
            {detailQuery.error instanceof ApiError
              ? detailQuery.error.message
              : 'Failed to load notification detail.'}
          </Alert>
        )}
        {detailQuery.data && (
          <div className="mt-4 space-y-6">
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Pair label="Template" value={detailQuery.data.codeName} />
              <Pair label="Status" value={humanizeEnum(detailQuery.data.status)} />
              <Pair label="Category" value={detailQuery.data.category} />
              <Pair label="Recipient" value={detailQuery.data.recipientEmail} />
              <Pair label="Created" value={detailQuery.data.createdAt} />
              <Pair label="Source event" value={detailQuery.data.sourceEventId} />
            </dl>

            <section>
              <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
                Deliveries ({detailQuery.data.deliveries.length})
              </h3>
              {detailQuery.data.deliveries.length === 0 ? (
                <p className="text-sm text-ink-muted">No deliveries.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-ink/5 text-left">
                    <tr>
                      <th className="px-3 py-2 w-24">Channel</th>
                      <th className="px-3 py-2 w-28">Status</th>
                      <th className="px-3 py-2 w-20">Attempts</th>
                      <th className="px-3 py-2 w-44">Dispatched</th>
                      <th className="px-3 py-2 w-44">Delivered</th>
                      <th className="px-3 py-2">Last error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailQuery.data.deliveries.map((d) => (
                      <tr key={d.id} className="border-t border-border">
                        <td className="px-3 py-2">{d.channel}</td>
                        <td className="px-3 py-2"><StatusBadge status={d.status} /></td>
                        <td className="px-3 py-2">{d.attemptCount}</td>
                        <td className="px-3 py-2">{d.dispatchedAt ?? '—'}</td>
                        <td className="px-3 py-2">{d.deliveredAt ?? '—'}</td>
                        <td className="px-3 py-2 text-ink-muted">
                          {d.lastErrorMessage ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section>
              <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
                Payload
              </h3>
              <pre className="text-xs bg-ink/5 p-3 rounded-md overflow-x-auto">
                {JSON.stringify(detailQuery.data.payload, null, 2)}
              </pre>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1 break-all">{value}</dd>
    </div>
  );
}
